// R115 maint — shared bits for the calendar hub's maintenance module: the
// routes it writes through (the Maintenance module's EXISTING work-order
// routes, tagged surface:'calendar'), page-life caches for the pickers, the
// per-WO UI state that survives an inspector re-render (half-typed comments,
// unsaved field edits — the R37 data-loss class), and the money rule.
import type { CalendarCtx, OfficeRole, ViewId } from '../registry';
import { canSeeWoMoney, type WoLayer } from '../../../../../lib/woCalendar.mjs';
import type { WoEditForm } from '../../../../../lib/woEdit.mjs';

export const AO = '/api/assetops';
export const WO_API = `${AO}/work-orders`;
export const LAYER_ID = 'wo_unscheduled';
export const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];
/** Every view id (incl. the Operations shortcuts) — sections the editor should open everywhere. */
export const ALL_VIEWS = ['stays', 'ops', 'ops.maint', 'ops.hk', 'guests', 'mtr', 'health'] as unknown as ViewId[];
export const MAINT_VIEWS = ['ops', 'ops.maint'] as unknown as ViewId[];

/** Money (cost / payer / billed / skill / labor $ / quote + invoice amounts) — admin, director, accounting. */
export const seesMoney = (ctx: Pick<CalendarCtx, 'role'>): boolean => canSeeWoMoney(ctx.role);

/** The layer, unless it is missing or the server said it couldn't read the store. */
export function woLayer(ctx: Pick<CalendarCtx, 'layer'>): WoLayer | null {
  const l = ctx.layer<WoLayer>(LAYER_ID);
  return l && !l.unavailable && Array.isArray(l.unscheduled) ? l : null;
}

// ── HTTP ─────────────────────────────────────────────────────────────────────
export interface ApiRes<T = any> { ok: boolean; status: number; json: T | null; error: string }
/** The server's own words when it has them (settingsGuard refusals, 409s, owner_handled …), never a bare "didn't save". */
export function errText(status: number, j: any): string {
  const m = j && (j.message || j.error);
  if (status === 403) return m && m !== 'money_hidden' && !/^Forbidden/.test(String(m)) ? String(m) : "Your role can't do that from the calendar.";
  if (status === 404) return 'That work order no longer exists — it may have been deleted.';
  if (status === 409) return m ? String(m) : 'That work order is already done.';
  if (m) return String(m);
  return status ? `The server answered HTTP ${status} — nothing was changed.` : 'No connection — nothing was saved. Try again.';
}
export async function api<T = any>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<ApiRes<T>> {
  try {
    const r = await fetch(url, {
      method,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
    const j = await r.json().catch(() => null);
    return { ok: r.ok, status: r.status, json: j, error: r.ok ? '' : errText(r.status, j) };
  } catch (e: any) {
    if (e && e.name === 'AbortError') throw e;
    return { ok: false, status: 0, json: null, error: errText(0, null) };
  }
}

// ── page-life caches for the pickers (10 min) ────────────────────────────────
export interface Vendor { id: string; name: string; archived?: boolean; hourly_rate?: number | null }
export interface Person { id?: string; full_name?: string; email?: string; role?: string }
export interface Asset { id: string; name: string; listing_id?: string | number | null }
const TTL = 10 * 60 * 1000;
let vendorsP: Promise<Vendor[]> | null = null, vendorsAt = 0;
let rosterP: Promise<Person[]> | null = null, rosterAt = 0;
const assetsP = new Map<string, { at: number; p: Promise<Asset[]> }>();
export function loadVendors(): Promise<Vendor[]> {
  if (!vendorsP || Date.now() - vendorsAt > TTL) {
    vendorsAt = Date.now();
    vendorsP = api(`GET`, `${AO}/vendors`).then(r => (r.ok && r.json && Array.isArray(r.json.vendors) ? r.json.vendors.map((v: any) => ({ id: String(v.id), name: String(v.name || ''), archived: !!v.archived, hourly_rate: v.hourly_rate ?? null })) : []))
      .catch(() => []);
    vendorsP.then(v => { if (!v.length) vendorsAt = 0; });   // a failed read is retried next time
  }
  return vendorsP;
}
export function loadRoster(): Promise<Person[]> {
  if (!rosterP || Date.now() - rosterAt > TTL) {
    rosterAt = Date.now();
    rosterP = api('GET', '/api/tasks/assignees').then(r => (r.ok && r.json && Array.isArray(r.json.people) ? r.json.people.map((p: any) => ({ id: p.id, full_name: p.full_name || '', role: p.role || '' })) : [])).catch(() => []);
    rosterP.then(p => { if (!p.length) rosterAt = 0; });
  }
  return rosterP;
}
export function loadAssets(listingId: string): Promise<Asset[]> {
  const hit = assetsP.get(listingId);
  if (hit && Date.now() - hit.at < TTL) return hit.p;
  const p = api('GET', `${AO}/assets?listing_id=${encodeURIComponent(listingId)}`).then(r => (r.ok && r.json && Array.isArray(r.json.assets) ? r.json.assets : [])).catch(() => []);
  assetsP.set(listingId, { at: Date.now(), p });
  return p;
}
/** Tests only. */
export function __resetCaches(): void { vendorsP = null; rosterP = null; vendorsAt = 0; rosterAt = 0; assetsP.clear(); ui.clear(); }

// ── per-WO UI state (survives inspector re-renders) ──────────────────────────
export interface WoUi {
  form: WoEditForm | null;       // unsaved fieldset values (null = showing the record)
  edits: Record<string, any> | null;   // R115 review: ONLY what the user changed (diffWoEdit vs the record the form was drawn from)
  dirty: boolean;                 // form differs from the record
  cmt: string; report: string; fu: boolean; fuNote: string; reviewNote: string; itemNote: Record<string, string>;
  editTitle: string | null; editDesc: string | null;
  busy: string; err: string; ok: string;
}
export const ui = new Map<string, WoUi>();
export function uiOf(id: string): WoUi {
  let u = ui.get(id);
  if (!u) { u = { form: null, edits: null, dirty: false, cmt: '', report: '', fu: false, fuNote: '', reviewNote: '', itemNote: {}, editTitle: null, editDesc: null, busy: '', err: '', ok: '' }; ui.set(id, u); }
  return u;
}
/** Typed-but-unsaved text anywhere in a WO's editor. */
export const hasUnsaved = (u: WoUi): boolean => !!(u.dirty || u.cmt.trim() || u.report.trim() || u.fuNote.trim() || u.reviewNote.trim() || u.editTitle !== null || u.editDesc !== null || Object.values(u.itemNote).some(v => String(v || '').trim()));
export const anyWoBusy = (): boolean => [...ui.values()].some(u => !!u.busy || hasUnsaved(u));

// ── small helpers ────────────────────────────────────────────────────────────
export const PRIO_TONE: Record<string, string> = { emergency: 'bad', urgent: 'warn', routine: 'info', pm: 'muted' };
export const PRIO_LABEL: Record<string, string> = { emergency: 'Emergency', urgent: 'Urgent', routine: 'Routine', pm: 'PM' };
export function wmd(d: string | null | undefined): string {
  if (!d) return '';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z');
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
export function md(d: string | null | undefined): string {
  if (!d) return '';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z');
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
export function ageText(days: number | null | undefined): string {
  if (days == null) return '';
  if (days < 1) return 'today';
  if (days < 14) return `${days}d old`;
  if (days < 60) return `${Math.round(days / 7)}w old`;
  return `${Math.round(days / 30)}mo old`;
}
/** Who is staying (or an owner stay / block) on a listing's night — for the guest-in-house warning.
 *  R115 review: a Hostaway 'held' night is a real booking the calendar can't see ('held'); a day outside the loaded
 *  window can't be checked at all ('unknown') — both ask instead of booking straight through. */
export type NightGuest = { kind: 'stay' | 'owner' | 'held' | 'unknown'; check_in: string; check_out: string };
export function nightGuest(ctx: CalendarCtx, listingId: string, date: string): NightGuest | null {
  const d = ctx.data;
  if (d && d.from && d.to && (date < d.from || date > d.to)) return { kind: 'unknown', check_in: date, check_out: date };
  for (const s of ctx.staysOf(listingId)) {
    if (!s || s.kind === 'inquiry' || s.hidden) continue;
    if (/cancel/i.test(String(s.status || ''))) continue;
    if (s.check_in <= date && date < s.check_out) return { kind: s.kind === 'owner' ? 'owner' : 'stay', check_in: s.check_in, check_out: s.check_out };
  }
  for (const b of (d && d.blocks) || []) if (b && b.listing_id === listingId && b.kind === 'held' && b.start <= date && date < b.end) return { kind: 'held', check_in: b.start, check_out: b.end };
  return null;
}
/** The warning line for a NightGuest (the guest-night ask and the new-WO sheet). */
export function nightWarn(g: NightGuest, day: string, esc: (s: unknown) => string): string {
  if (g.kind === 'unknown') return `📅 The calendar hasn't loaded ${esc(day)} — a guest may be staying. Check the home is empty (or get permission) before sending a tech.`;
  if (g.kind === 'held') return `🔒 Those nights are held in Hostaway (${esc(md(g.check_in))} → ${esc(md(g.check_out))}) — likely a booking this calendar can't show. The tech may need permission to go in.`;
  return `🛏 ${g.kind === 'owner' ? 'The owner is' : 'A guest is'} staying that night (${esc(md(g.check_in))} → ${esc(md(g.check_out))}). The tech needs permission to go in while they're there.`;
}
